import React from 'react';
import { Link } from 'react-router-dom';
import { openCookieSettings } from '../lib/consent';
import { Phone, Mail, MapPin, Instagram, Facebook, Heart } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { LOCAL_PAGES } from '../data/localPages';

// Custom TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Accueil', href: '#' },
    { label: 'Services', href: '#services' },
    { label: 'Crédit d\'impôt 50 %', href: '/credit-impot-50-particuliers' },
    { label: 'Blog', href: '/blog' },
    { label: 'Avis', href: '#avis' },
    { label: 'Réservation', href: '#reservation' },
    { label: 'Contact', href: '#contact' }
  ];

  const serviceLinks = [
    'Nettoyage de vitres',
    'Nettoyage de véranda',
    'Ménage professionnels',
    'Ménage particuliers (-50% impôts)',
    'Nettoyage fin de chantier',
    'Nettoyage moquette & canapé',
    'Nettoyage des encadrements',
    'Nettoyage des volets',
    'Nettoyage de terrasse'
  ];

  // Pages SEO locales auto-listées depuis la config — pas besoin de toucher
  // au Footer quand on ajoute une nouvelle ville.
  const localPages = Object.values(LOCAL_PAGES).map((p) => ({
    to: `/${p.slug}`,
    // Affichage compact : "Vitres · La Tremblade"
    label: `${p.service.replace(/^Nettoyage (de |des )?/i, '').replace(/^./, (c) => c.toUpperCase())} · ${p.city}`,
  }));

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-24 w-auto mb-6 drop-shadow-lg"
            />
            <p className="text-gray-400 mb-6 leading-relaxed">
              Votre spécialiste du nettoyage de vitres et vérandas en Charente-Maritime. Qualité professionnelle, résultat garanti.
            </p>
            <div className="flex gap-3">
              <a
                href={`https://instagram.com/${companyInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-gradient-to-br hover:from-purple-500 hover:via-pink-500 hover:to-orange-400 flex items-center justify-center transition-all duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-[#1877F2] flex items-center justify-center transition-all duration-300"
              >
                <Facebook className="w-5 h-5" fill="currentColor" />
              </a>
              <a
                href={`https://tiktok.com/@${companyInfo.tiktok}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-black flex items-center justify-center transition-all duration-300"
              >
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-[hsl(32,95%,55%)] transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Nos services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((service) => (
                <li key={service}>
                  <span className="text-gray-400">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pages SEO locales — auto-listées depuis LOCAL_PAGES */}
          {localPages.length > 0 && (
            <div data-testid="footer-local-pages">
              <h4 className="text-lg font-semibold mb-6">Nos villes</h4>
              <ul className="space-y-3">
                {localPages.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      className="text-gray-400 hover:text-[hsl(32,95%,55%)] transition-colors duration-200 text-sm"
                      data-testid={`footer-local-link${p.to}`}
                    >
                      {p.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-[hsl(207,90%,54%)] transition-colors"
                >
                  <Phone className="w-5 h-5 text-[hsl(207,90%,54%)]" />
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-[hsl(207,90%,54%)] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[hsl(207,90%,54%)]" />
                  {companyInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-[hsl(207,90%,54%)]" />
                  {companyInfo.location}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {currentYear} {companyInfo.name}. Tous droits réservés.
            </p>
            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm" aria-label="Informations légales">
              <Link to="/politique-de-confidentialite" className="text-gray-400 hover:text-[hsl(207,90%,54%)] transition-colors" data-testid="footer-privacy-link">Politique de confidentialité</Link>
              <Link to="/conditions-generales-utilisation" className="text-gray-400 hover:text-[hsl(207,90%,54%)] transition-colors" data-testid="footer-terms-link">Conditions générales d'utilisation</Link>
              <button type="button" onClick={openCookieSettings} className="text-gray-400 hover:text-[hsl(207,90%,54%)] transition-colors" data-testid="footer-cookie-settings">Gestion des cookies</button>
            </nav>
            <p className="text-gray-500 text-sm flex items-center gap-1">
              Fait avec <Heart className="w-4 h-4 text-red-500 fill-current" /> en Charente-Maritime
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
